import {FileText, Link2, Mic, Square, Type, UploadCloud} from 'lucide-react';
import {FormEvent, useEffect, useRef, useState} from 'react';
import {api, ApiError} from '../api';
import {Modal, PrimaryButton, SecondaryButton, TextInput, Textarea} from '../design';
import {CareerEvidence} from '../types';

type InputKind = CareerEvidence['kind'];

const MAX_BYTES = 5 * 1024 * 1024;
export const MAX_RECORDING_MS = 5 * 60 * 1000;
const fileAccept = '.pdf,.docx,.txt,.md,.markdown,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain,text/markdown';

const recordingClock = (seconds: number): string =>
  `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`;

const errorMessage = (error: unknown): string =>
  error instanceof ApiError || error instanceof Error ? error.message : 'The evidence could not be saved.';

export const EvidenceCapture = ({
  opened,
  onClose,
  onSaved,
}: {
  opened: boolean;
  onClose: () => void;
  onSaved: (record: CareerEvidence) => void | Promise<void>;
}) => {
  const [kind, setKind] = useState<InputKind>('text');
  const [label, setLabel] = useState('');
  const [text, setText] = useState('');
  const [url, setUrl] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [recording, setRecording] = useState(false);
  const [recordingSeconds, setRecordingSeconds] = useState(0);
  const [voiceFile, setVoiceFile] = useState<File | null>(null);
  const [pendingTranscript, setPendingTranscript] = useState<CareerEvidence | null>(null);
  const [transcript, setTranscript] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const recorder = useRef<MediaRecorder | null>(null);
  const stream = useRef<MediaStream | null>(null);
  const chunks = useRef<Blob[]>([]);
  const recordingTimeout = useRef<number | undefined>(undefined);
  const recordingInterval = useRef<number | undefined>(undefined);
  const discardRecording = useRef(false);
  const captureGeneration = useRef(0);

  const clearRecordingTimers = () => {
    if (recordingTimeout.current !== undefined) window.clearTimeout(recordingTimeout.current);
    if (recordingInterval.current !== undefined) window.clearInterval(recordingInterval.current);
    recordingTimeout.current = undefined;
    recordingInterval.current = undefined;
  };

  const stopTracks = () => {
    stream.current?.getTracks().forEach((track) => track.stop());
    stream.current = null;
  };

  const discardActiveRecording = () => {
    captureGeneration.current += 1;
    discardRecording.current = true;
    clearRecordingTimers();
    const active = recorder.current;
    if (active?.state === 'recording') active.stop();
    else {
      recorder.current = null;
      stopTracks();
    }
    chunks.current = [];
    setRecording(false);
    setRecordingSeconds(0);
  };

  useEffect(() => () => {
    captureGeneration.current += 1;
    discardRecording.current = true;
    clearRecordingTimers();
    const active = recorder.current;
    if (active) {
      active.ondataavailable = null;
      active.onstop = null;
      if (active.state === 'recording') active.stop();
    }
    recorder.current = null;
    chunks.current = [];
    stopTracks();
  }, []);

  const reset = () => {
    discardActiveRecording();
    setLabel('');
    setText('');
    setUrl('');
    setFile(null);
    setVoiceFile(null);
    setPendingTranscript(null);
    setTranscript('');
    setError('');
  };

  const close = () => {
    if (busy) return;
    reset();
    onClose();
  };

  const startRecording = async () => {
    setError('');
    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === 'undefined') {
      setError('Voice recording is not supported by this browser. Upload an audio file from another recorder instead.');
      return;
    }
    discardActiveRecording();
    discardRecording.current = false;
    setVoiceFile(null);
    const requestedGeneration = captureGeneration.current;
    try {
      const activeStream = await navigator.mediaDevices.getUserMedia({audio: true, video: false});
      if (requestedGeneration !== captureGeneration.current) {
        activeStream.getTracks().forEach((track) => track.stop());
        return;
      }
      stream.current = activeStream;
      chunks.current = [];
      const preferredType = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4'].find((value) =>
        MediaRecorder.isTypeSupported(value),
      );
      const activeRecorder = new MediaRecorder(activeStream, preferredType ? {mimeType: preferredType} : undefined);
      recorder.current = activeRecorder;
      activeRecorder.ondataavailable = (event) => {
        if (event.data.size) chunks.current.push(event.data);
      };
      activeRecorder.onstop = () => {
        clearRecordingTimers();
        const type = activeRecorder.mimeType || 'audio/webm';
        const blob = new Blob(chunks.current, {type});
        if (!discardRecording.current) {
          if (blob.size > MAX_BYTES) {
            setVoiceFile(null);
            setError('That recording is larger than 5 MB. Record a shorter story or upload a smaller audio file.');
          } else if (blob.size > 0) {
            const extension = type.includes('mp4') ? 'm4a' : 'webm';
            setVoiceFile(new File([blob], `career-story-${Date.now()}.${extension}`, {type}));
          } else {
            setVoiceFile(null);
            setError('No audio was captured. Check the microphone and try again.');
          }
        }
        recorder.current = null;
        chunks.current = [];
        activeStream.getTracks().forEach((track) => track.stop());
        if (stream.current === activeStream) stream.current = null;
        setRecording(false);
        setRecordingSeconds(0);
      };
      activeRecorder.start(500);
      setRecording(true);
      setRecordingSeconds(0);
      const startedAt = Date.now();
      recordingInterval.current = window.setInterval(() => {
        setRecordingSeconds(Math.min(300, Math.floor((Date.now() - startedAt) / 1_000)));
      }, 1_000);
      recordingTimeout.current = window.setTimeout(() => {
        if (activeRecorder.state === 'recording') activeRecorder.stop();
      }, MAX_RECORDING_MS);
    } catch {
      stopTracks();
      setError('Microphone access was not available. You can retry or upload an audio file instead.');
    }
  };

  const stopRecording = () => {
    clearRecordingTimers();
    if (recorder.current?.state === 'recording') recorder.current.stop();
    else {
      stopTracks();
      setRecording(false);
      setRecordingSeconds(0);
    }
  };

  const chooseFile = (next: File | null, voice = false) => {
    setError('');
    if (!next) return voice ? setVoiceFile(null) : setFile(null);
    if (next.size > MAX_BYTES) {
      setError('Files may not exceed 5 MB. Choose a smaller source.');
      return voice ? setVoiceFile(null) : setFile(null);
    }
    voice ? setVoiceFile(next) : setFile(next);
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError('');
    try {
      let saved: CareerEvidence;
      if (kind === 'text') saved = await api.addTextEvidence({label: label || 'Career note', text});
      else if (kind === 'link') saved = await api.addLinkEvidence({label: label || 'Public profile or project', url});
      else if (kind === 'document' && file) saved = await api.uploadEvidence(file, 'document', label || file.name);
      else if (kind === 'voice' && voiceFile) saved = await api.uploadEvidence(voiceFile, 'voice', label || 'Recorded career story');
      else throw new Error('Choose or record the evidence before saving.');

      if (saved.kind === 'voice' && saved.state === 'transcript_review') {
        setPendingTranscript(saved);
        setTranscript(saved.transcript ?? '');
      } else {
        await onSaved(saved);
        reset();
        onClose();
      }
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setBusy(false);
    }
  };

  const approveTranscript = async () => {
    if (!pendingTranscript || !transcript.trim()) return;
    setBusy(true);
    setError('');
    try {
      const saved = await api.approveTranscript(pendingTranscript.id, transcript);
      await onSaved(saved);
      reset();
      onClose();
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal opened={opened} onClose={close} title="Add career evidence" size="lg" centered>
      {pendingTranscript ? (
        <div className="pd-evidence-review">
          <span className="pd-eyebrow">Transcript review required</span>
          <p>Correct the transcript before it becomes source-backed career evidence. Nothing is confirmed automatically.</p>
          <Textarea
            label="Editable transcript"
            value={transcript}
            autosize
            minRows={8}
            onChange={(event) => setTranscript(event.currentTarget.value)}
          />
          {error ? <p className="pd-form-error" role="alert">{error}</p> : null}
          <div className="pd-form-actions">
            <SecondaryButton disabled={busy} onClick={() => setPendingTranscript(null)}>Back</SecondaryButton>
            <PrimaryButton loading={busy} disabled={!transcript.trim()} onClick={() => void approveTranscript()}>
              Confirm corrected transcript
            </PrimaryButton>
          </div>
        </div>
      ) : (
        <form className="pd-evidence-form" onSubmit={(event) => void submit(event)}>
          <div className="pd-evidence-kind" role="tablist" aria-label="Evidence input type">
            {([
              ['text', Type, 'Text'],
              ['link', Link2, 'Public link'],
              ['document', FileText, 'Document'],
              ['voice', Mic, 'Voice'],
            ] as const).map(([value, Icon, title]) => (
              <button
                key={value}
                type="button"
                role="tab"
                aria-selected={kind === value}
                className={kind === value ? 'is-active' : ''}
                onClick={() => {
                  if (kind === 'voice' && value !== 'voice' && recording) discardActiveRecording();
                  setKind(value);
                  setError('');
                }}
              >
                <Icon size={18} aria-hidden="true" />
                {title}
              </button>
            ))}
          </div>

          <TextInput
            label="Source label"
            description="A short name you will recognize in provenance views."
            value={label}
            onChange={(event) => setLabel(event.currentTarget.value)}
            placeholder="Example: Platform migration retrospective"
          />

          {kind === 'text' ? (
            <Textarea
              required
              label="Career evidence"
              value={text}
              autosize
              minRows={7}
              onChange={(event) => setText(event.currentTarget.value)}
              placeholder="Describe the work, your role, the outcome, and anything that can be verified…"
            />
          ) : null}
          {kind === 'link' ? (
            <TextInput
              required
              type="url"
              label="Public HTTPS link"
              description="Private networks, localhost, cloud metadata, credentials, and reserved addresses are blocked server-side."
              value={url}
              onChange={(event) => setUrl(event.currentTarget.value)}
              placeholder="https://github.com/…"
            />
          ) : null}
          {kind === 'document' ? (
            <label className="pd-compact-upload">
              <UploadCloud size={24} aria-hidden="true" />
              <strong>{file?.name ?? 'Choose a supporting document'}</strong>
              <span>PDF, DOCX, TXT, or Markdown · 5 MB maximum</span>
              <input type="file" accept={fileAccept} onChange={(event) => chooseFile(event.currentTarget.files?.[0] ?? null)} />
            </label>
          ) : null}
          {kind === 'voice' ? (
            <div className="pd-voice-capture">
              <div>
                <Mic size={23} aria-hidden="true" />
                <strong>{recording ? 'Recording…' : voiceFile ? voiceFile.name : 'Record a career story'}</strong>
                <span>
                  {recording
                    ? recordingClock(recordingSeconds) + ' of 5:00 maximum'
                    : 'Five-minute and 5 MB maximum. You will review and correct the transcript before it becomes evidence.'}
                </span>
              </div>
              {recording ? (
                <SecondaryButton type="button" leftSection={<Square size={14} />} onClick={stopRecording}>Stop</SecondaryButton>
              ) : (
                <SecondaryButton type="button" leftSection={<Mic size={14} />} onClick={() => void startRecording()}>
                  {voiceFile ? 'Record again' : 'Start recording'}
                </SecondaryButton>
              )}
              <label className="pd-audio-upload">
                or upload audio
                <input
                  type="file"
                  accept="audio/mpeg,audio/mp4,audio/wav,audio/webm,.mp3,.m4a,.wav,.webm"
                  onChange={(event) => chooseFile(event.currentTarget.files?.[0] ?? null, true)}
                />
              </label>
            </div>
          ) : null}

          {error ? <p className="pd-form-error" role="alert">{error}</p> : null}
          <div className="pd-form-actions">
            <SecondaryButton type="button" disabled={busy} onClick={close}>Cancel</SecondaryButton>
            <PrimaryButton
              type="submit"
              loading={busy}
              disabled={recording || (kind === 'text' && !text.trim()) || (kind === 'link' && !url.trim()) || (kind === 'document' && !file) || (kind === 'voice' && !voiceFile)}
            >
              {kind === 'voice' ? 'Transcribe for review' : 'Save as evidence'}
            </PrimaryButton>
          </div>
        </form>
      )}
    </Modal>
  );
};
